import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import {
  RocketLaunchIcon,
  LightBulbIcon,
  HeartIcon,
  ShieldCheckIcon,
  UsersIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';

const About = () => {
  const values = [
    {
      icon: RocketLaunchIcon,
      title: 'Innovation',
      description: 'We continuously improve our platform with cutting-edge technology to help businesses thrive.',
    },
    {
      icon: HeartIcon,
      title: 'Customer First',
      description: 'Your success is our success. We listen to feedback and build features that matter to you.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Reliability',
      description: 'We provide a stable, secure platform you can depend on for your critical business operations.',
    },
    {
      icon: LightBulbIcon,
      title: 'Simplicity',
      description: 'Complex problems deserve simple solutions. We make inventory management easy and intuitive.',
    },
  ];

  const stats = [
    { value: 'Growing', label: 'Happy Customers' },
    { value: '99.9%', label: 'Uptime' },
    { value: '24/7', label: 'Support' },
    { value: '2026', label: 'Founded' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            About StockWise Inventory
          </h1>
          <p className="text-xl text-primary-100 max-w-3xl mx-auto">
            We're on a mission to help businesses of all sizes manage their inventory efficiently with modern, cloud-based technology.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  StockWise Inventory was born from a simple observation: businesses were struggling with outdated, complex inventory management systems that didn't meet their needs. We saw companies using spreadsheets, paper records, or expensive enterprise software that was difficult to use.
                </p>
                <p>
                  We believed there had to be a better way. A solution that was powerful yet simple, affordable yet feature-rich, and accessible from anywhere. That's why we created StockWise Inventory.
                </p>
                <p>
                  Today, we're proud to serve businesses across Ethiopia and beyond, helping them streamline their operations, reduce costs, and grow with confidence. Our cloud-based platform combines the best of modern technology with an intuitive interface that anyone can use.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-primary-50 to-cyan-50 rounded-2xl p-8 md:p-12">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center">
                      <GlobeAltIcon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Global Reach</h3>
                    <p className="text-gray-700">
                      Serving businesses worldwide with multi-currency support and localization.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center">
                      <UsersIcon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Customer Focused</h3>
                    <p className="text-gray-700">
                      Built with feedback from real businesses to solve real problems.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center">
                      <RocketLaunchIcon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Always Improving</h3>
                    <p className="text-gray-700">
                      Regular updates and new features based on your needs and industry trends.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-primary-600 mb-2">
                  {stat.value}
                </p>
                <p className="text-gray-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Values
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              These core values guide everything we do and shape how we build our product and serve our customers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
                    <Icon className="w-8 h-8 text-primary-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-gray-600">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
              <p className="text-gray-700 leading-relaxed">
                To empower businesses with simple, powerful inventory management tools that help them operate more efficiently, reduce costs, and focus on growth. We believe every business, regardless of size, deserves access to professional-grade inventory management software.
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h2>
              <p className="text-gray-700 leading-relaxed">
                To become the leading inventory management platform in Africa and beyond, known for our innovation, reliability, and customer-centric approach. We envision a future where every business can manage their inventory with confidence and ease.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Technology */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Built with Modern Technology
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We use cutting-edge technology to deliver a fast, secure, and reliable platform that scales with your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Cloud-Based</h3>
              <p className="text-gray-700">
                Access your inventory from anywhere, anytime. No installation required. Automatic updates and backups included.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Secure & Reliable</h3>
              <p className="text-gray-700">
                Enterprise-grade security with encryption, regular backups, and 99.9% uptime. Your data is safe with us.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Mobile Friendly</h3>
              <p className="text-gray-700">
                Fully responsive design works perfectly on phones, tablets, and desktops. Manage inventory on the go.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 gradient-bg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Inventory Management?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Join growing businesses using StockWise Inventory. Start your free 7-day trial today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/request-trial" className="btn-primary bg-white text-primary-600 hover:bg-gray-100">
              Start Free Trial
            </Link>
            <Link to="/contact" className="btn-outline">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
